import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useBooking, isWeekend, NEXT_DAY_SLOTS, SPORT_COURT_COUNT } from "../context/BookingContext";
import { usePricing, isNightSlot } from "../context/PricingContext";
import { usePromo } from "../context/PromoContext";

// ── Constants ──────────────────────────────────────────────────────────────────
const SPORTS = [
  { id: "oval",       icon: "🏏", name: "Oval Cricket",   courts: "1 Ground",  courtKey: "Oval Cricket Ground", defaultPrice: 800 },
  { id: "box",        icon: "🏏", name: "Box Cricket",    courts: "2 Courts",  courtKey: "Box Cricket Court 1", defaultPrice: 500 },
  { id: "pickleball", icon: "🎾", name: "Pickleball",     courts: "2 Courts",  courtKey: "Pickleball Court 1",  defaultPrice: 300 },
];


function getDates() {
  return Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split("T")[0];
  });
}

function getSlots() {
  const slots = [];
  // 5 AM to 11 PM
  for (let h = 5; h < 24; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
  }
  // 12 AM–4 AM (next day)
  slots.push("00:00");
  slots.push("01:00");
  slots.push("02:00");
  slots.push("03:00");
  slots.push("04:00");
  return slots;
}

const DATES = getDates();
const SLOTS = getSlots();
const DAY_NAMES   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Map each slot to its absolute hour position within the 05:00–04:00 day window.
// Late-night slots (00–04) wrap past midnight so they sort after 23:00.
function slotHour(t) {
  const h = parseInt(t, 10);
  return h < 5 ? h + 24 : h;
}

function isConsecutive(slots) {
  if (slots.length <= 1) return true;
  const sorted = [...slots].sort((a, b) => slotHour(a) - slotHour(b));
  for (let i = 1; i < sorted.length; i++) {
    if (slotHour(sorted[i]) - slotHour(sorted[i - 1]) !== 1) return false;
  }
  return true;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return { day: DAY_NAMES[d.getDay()], date: d.getDate(), month: MONTH_NAMES[d.getMonth()] };
}

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function formatRange(t) {
  const h = parseInt(t, 10);
  const endH = (h + 1) % 24;
  const startPeriod = h >= 12 ? "PM" : "AM";
  const endPeriod   = endH >= 12 ? "PM" : "AM";
  const startStr = `${h % 12 || 12}:00`;
  const endStr   = `${endH % 12 || 12}:00`;
  return startPeriod === endPeriod
    ? `${startStr} - ${endStr} ${startPeriod}`
    : `${startStr} ${startPeriod} - ${endStr}`;
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function BookSlot() {
  const navigate = useNavigate();
  const { selectedDate, setSelectedDate, selectedSlots, toggleSlot, clearSlots, calcTotal, blockedDates, bookings, blocks, setSelectedSport: setContextSport, expirePendingBookings } = useBooking();
  const { pricing, getSportRate, getSlotPrice } = usePricing();

  const { promo, calcDiscount } = usePromo();

  // Default to Oval Cricket on load
  const [selectedSport, setSelectedSport] = useState(SPORTS[0]);
  const dateRef = useRef(null);

  // Sync default sport into context and expire stale pending bookings on mount
  useEffect(() => {
    setContextSport(SPORTS[0]);
    expirePendingBookings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive booked slots for the selected date AND sport from Firestore bookings.
  // Regular slots (05:00–23:00) live in b.slots; late-night slots (00:00–04:00)
  // are stored in b.nextDaySlots and represent early morning of selectedDate+1.
  const activeForDate = bookings.filter(
    (b) =>
      b.date === selectedDate &&
      b.sport === selectedSport?.name &&
      !["cancelled", "failed"].includes(b.status)
  );

  // "failed" bookings younger than 2 minutes whose webhook hasn't confirmed yet.
  // These lock the slot to give Razorpay's webhook time to arrive and flip the
  // status to "confirmed" before another user can grab the same slot.
  const TWO_MIN_MS = 2 * 60 * 1000;
  const recentFailures = bookings.filter((b) => {
    if (b.status !== "failed" || b.webhookConfirmed) return false;
    if (b.date !== selectedDate || b.sport !== selectedSport?.name) return false;
    const ts = b.createdAt;
    const ms = ts?.toMillis?.()
      ?? (ts?.seconds != null ? ts.seconds * 1000 : typeof ts === "string" ? new Date(ts).getTime() : null);
    return ms !== null && Date.now() - ms < TWO_MIN_MS;
  });
  const bookedRegular = activeForDate.flatMap((b) => b.slots || []);
  const bookedNextDay = activeForDate.flatMap((b) => b.nextDaySlots || []);

  const nextDayLabel = (() => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  })();

  const weekend    = isWeekend(selectedDate);
  const courtKey   = selectedSport?.courtKey || "Oval Cricket Ground";
  const courtData  = pricing[courtKey];
  const dayRate    = courtData
    ? (weekend ? Number(courtData.weekendDay) : Number(courtData.weekdayDay))
    : getSportRate(selectedDate, selectedSport?.name);
  const nightRate  = courtData
    ? (weekend ? Number(courtData.weekendNight) : Number(courtData.weekdayNight))
    : dayRate;
  const isBlocked  = blockedDates.includes(selectedDate);
  const rawTotal    = calcTotal();
  const consecutive = isConsecutive(selectedSlots);
  // For promo discount calculation, use the day rate as the reference rate
  const discount    = consecutive ? calcDiscount(rawTotal, selectedSlots.length, dayRate) : 0;
  const finalTotal  = rawTotal - discount;

  // Day/night breakdown for sticky bar
  const daySlots   = selectedSlots.filter((s) => !isNightSlot(s));
  const nightSlots = selectedSlots.filter((s) =>  isNightSlot(s));
  const daySubtotal   = daySlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);
  const nightSubtotal = nightSlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);

  // Blocked slots from admin blocks collection for this date + sport
  const sportName = selectedSport?.name || "Oval Cricket";
  const totalCourts = SPORT_COURT_COUNT[sportName] || 1;

  const courtMatchesSport = (courts) => {
    if (!courts || courts.includes("all")) return true;
    if (sportName === "Oval Cricket") return courts.some((c) => c === "Oval Cricket" || c.startsWith("Oval"));
    if (sportName === "Box Cricket")  return courts.some((c) => c.startsWith("Box Cricket"));
    if (sportName === "Pickleball")   return courts.some((c) => c.startsWith("Pickleball"));
    return false;
  };

  const courtsBlockedByEntry = (bl) => {
    const courts = bl.courts || ["all"];
    if (courts.includes("all")) return totalCourts;
    if (sportName === "Box Cricket") return courts.filter((c) => c.startsWith("Box Cricket")).length;
    if (sportName === "Pickleball")  return courts.filter((c) => c.startsWith("Pickleball")).length;
    return 1;
  };

  const blocksForDate = blocks.filter((bl) => bl.date === selectedDate && courtMatchesSport(bl.courts));

  const slotStatus = (slot) => {
    const isNextDay = NEXT_DAY_SLOTS.has(slot);

    // For regular slots: check blocksForDate.slots and activeForDate.slots
    // For nextDay slots: check blocksForDate.nextDaySlots and activeForDate.nextDaySlots
    // In both cases only selectedDate records are relevant — prevDate records point to
    // a different calendar day (selectedDate, not selectedDate+1) and must not be used here.
    const relevantBlocks = isNextDay
      ? blocksForDate.filter((bl) => (bl.nextDaySlots || []).includes(slot))
      : blocksForDate.filter((bl) => (bl.slots       || []).includes(slot));
    const blockedCount = relevantBlocks.reduce((sum, bl) => sum + courtsBlockedByEntry(bl), 0);

    const bookedCount = isNextDay
      ? activeForDate.filter((b) => (b.nextDaySlots || []).includes(slot)).length
        + recentFailures.filter((b) => (b.nextDaySlots || []).includes(slot)).length
      : activeForDate.filter((b) => (b.slots || []).includes(slot)).length
        + recentFailures.filter((b) => (b.slots || []).includes(slot)).length;

    const unavailable = Math.min(blockedCount + bookedCount, totalCourts);
    if (unavailable >= totalCourts) {
      return blockedCount >= totalCourts ? "blocked" : "booked";
    }
    if (selectedSlots.includes(slot)) return "selected";
    return "available";
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    setContextSport(sport);
    clearSlots();
    // On mobile, scroll down to the date selector
    if (window.innerWidth < 768) {
      setTimeout(() => {
        dateRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div className="bg-white" style={{ paddingTop: 64 }}>

      {/* ── Promo banner — only shown when promotion is active ── */}
      {promo.active && (
        <div className="bg-[#f59e0b]/10 border-b border-[#f59e0b]/20 py-2.5 px-4">
          <p className="text-center text-[#b45309] text-sm font-medium">
            🎁 <span className="text-[#f59e0b] font-black">Promotional Offer:</span> {promo.bannerText}
          </p>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-8 pb-8">

        {/* ── Page header ── */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <h1
            className="text-black font-black text-3xl sm:text-4xl tracking-tight mb-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Book a Slot
          </h1>
          <p className="text-black/40 text-sm mb-8">Select a sport, pick a date, and choose your time</p>
        </motion.div>

        {/* ── Step 1: Sport selector ── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 }}>
          <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-3">
            Step 1 — Select a Sport
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
            {SPORTS.map((sport) => {
              const active = selectedSport?.id === sport.id;
              const cp = pricing?.[sport.courtKey];
              const liveDayPrice   = cp ? (weekend ? Number(cp.weekendDay)   : Number(cp.weekdayDay))   : sport.defaultPrice;
              const liveNightPrice = cp ? (weekend ? Number(cp.weekendNight) : Number(cp.weekdayNight)) : sport.defaultPrice;
              return (
                <div key={sport.id} className="relative">
                  <button
                    onClick={() => handleSportSelect(sport)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                      active
                        ? "border-[#f59e0b] bg-[#fffbeb] shadow-lg shadow-[#f59e0b]/15 hover:-translate-y-0.5"
                        : "border-black/8 bg-white hover:border-[#f59e0b]/50 hover:shadow-md hover:-translate-y-0.5"
                    }`}
                  >
                    <span className="text-3xl flex-shrink-0">{sport.icon}</span>
                    <div className="min-w-0">
                      <p className={`font-black text-sm leading-tight ${active ? "text-black" : "text-black/80"}`}>
                        {sport.name}
                      </p>
                      <p className="text-black/40 text-xs mt-0.5">{sport.courts}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs font-bold ${active ? "text-[#f59e0b]" : "text-black/60"}`}>
                          ☀️ ₹{Number(liveDayPrice).toLocaleString()}
                        </span>
                        <span className="text-black/20 text-xs">·</span>
                        <span className={`text-xs font-bold ${active ? "text-[#f59e0b]" : "text-black/60"}`}>
                          🌙 ₹{Number(liveNightPrice).toLocaleString()}
                        </span>
                        <span className="text-black/30 text-[10px] font-normal">/ hr</span>
                      </div>
                    </div>
                    {active && (
                      <span className="ml-auto w-5 h-5 rounded-full bg-[#f59e0b] flex items-center justify-center text-black text-xs font-black flex-shrink-0">
                        ✓
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* ── Steps 2 & 3: Date + Slots ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>

          {/* ── Step 2: Date selector ── */}
          <div ref={dateRef}>
            <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-3">
              Step 2 — Select a Date
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-8" style={{ scrollbarWidth: "none" }}>
              {DATES.map((d) => {
                const { day, date, month } = formatDate(d);
                const active  = d === selectedDate;
                const blocked = blockedDates.includes(d);
                const wknd    = isWeekend(d);
                return (
                  <button
                    key={d}
                    onClick={() => { if (!blocked) { setSelectedDate(d); clearSlots(); } }}
                    disabled={blocked}
                    className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-xl border transition-all duration-150 ${
                      active
                        ? "bg-[#f59e0b] border-[#f59e0b] shadow-md shadow-[#f59e0b]/20"
                        : blocked
                        ? "opacity-30 cursor-not-allowed bg-white border-black/8"
                        : "bg-white border-black/8 hover:border-[#f59e0b]/50 hover:shadow-sm"
                    }`}
                    style={{ minWidth: 60 }}
                  >
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${active ? "text-black/60" : "text-black/35"}`}>{day}</span>
                    <span className={`text-lg font-black leading-tight ${active ? "text-black" : "text-black"}`}>{date}</span>
                    <span className={`text-[10px] ${active ? "text-black/50" : "text-black/30"}`}>{month}</span>
                    {wknd && !active && <span className="text-[8px] text-[#f59e0b] font-black mt-0.5 tracking-wider">WKND</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Step 3: Slot grid ── */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em]">
              Step 3 — Choose Time Slots
            </p>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border ${
                weekend
                  ? "bg-[#fffbeb] border-[#f59e0b]/30 text-[#b45309]"
                  : "bg-black/[0.03] border-black/8 text-black/50"
              }`}>
                {weekend ? "⚡ Weekend" : "📅 Weekday"}
                <span className="text-black/20">·</span>
                <span>☀️ ₹{dayRate.toLocaleString()}</span>
                <span className="text-black/20">·</span>
                <span>🌙 ₹{nightRate.toLocaleString()}</span>
              </div>
              <div className="hidden sm:flex items-center gap-3 text-[11px] text-black/40">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#D4F4DD] border border-green-300 inline-block" />Available</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#9ca3af] inline-block" />Booked</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-200 border border-red-300 inline-block" />Blocked</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#f59e0b] inline-block" />Selected</span>
              </div>
            </div>
          </div>

          {isBlocked ? (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center">
              <p className="text-red-500 font-semibold mb-1">🚫 Ground is closed on this date</p>
              <p className="text-red-400/70 text-sm">Please choose another date</p>
            </div>
          ) : (
            <div className="bg-white border border-black/8 rounded-2xl p-4 sm:p-5">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {SLOTS.map((slot) => {
                  const status  = slotStatus(slot);
                  const isNight = isNightSlot(slot);
                  return (
                    <motion.button
                      key={slot}
                      onClick={() => (status === "available" || status === "selected") && toggleSlot(slot)}
                      disabled={status === "booked" || status === "blocked"}
                      whileTap={status === "available" || status === "selected" ? { scale: 0.93 } : {}}
                      className={`py-2.5 px-1 rounded-xl text-xs font-semibold transition-all border flex flex-col items-center gap-0.5 ${
                        status === "booked"
                          ? "bg-[#9ca3af] border-[#9ca3af] text-white cursor-not-allowed opacity-60"
                          : status === "blocked"
                          ? "bg-red-100 border-red-300 text-red-500 cursor-not-allowed opacity-70"
                          : status === "selected"
                          ? "bg-[#f59e0b] border-[#f59e0b] text-black shadow-md shadow-[#f59e0b]/25"
                          : isNight
                          ? "bg-[#C8F0D8] border-green-300 text-green-900 hover:bg-[#BBE8CC] hover:border-green-400 cursor-pointer"
                          : "bg-[#D4F4DD] border-green-200 text-green-800 hover:bg-[#BBF0CA] hover:border-green-300 cursor-pointer"
                      }`}
                    >
                      <span className="text-[8px] leading-none mb-0.5">{isNight ? "🌙" : "☀️"}</span>
                      {formatRange(slot)}
                      {status === "booked" ? (
                        <span className="text-[8px] font-black leading-none tracking-wide text-white/80">BOOKED</span>
                      ) : status === "blocked" ? (
                        <span className="text-[8px] font-bold leading-none tracking-wide text-red-400">Blocked</span>
                      ) : NEXT_DAY_SLOTS.has(slot) ? (
                        <span className="text-[8px] font-bold opacity-50 leading-none tracking-wide">
                          {nextDayLabel}
                        </span>
                      ) : null}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

      </div>

      {/* ── Sticky bottom bar ── */}
      {selectedSlots.length > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-black/8 shadow-2xl shadow-black/10"
        >
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            {/* Left: slot count + day/night breakdown + promo status */}
            <div className="min-w-0">
              <p className="text-black font-bold text-sm leading-tight">
                {selectedSlots.length} slot{selectedSlots.length > 1 ? "s" : ""} selected
                <span className="text-black/40 font-normal"> · {selectedSlots.length} hr{selectedSlots.length > 1 ? "s" : ""}</span>
              </p>
              {(daySlots.length > 0 || nightSlots.length > 0) && (
                <p className="text-black/40 text-xs mt-0.5">
                  {daySlots.length > 0 && <span>☀️ {daySlots.length} day ₹{daySubtotal.toLocaleString()}</span>}
                  {daySlots.length > 0 && nightSlots.length > 0 && <span className="mx-1.5">+</span>}
                  {nightSlots.length > 0 && <span>🌙 {nightSlots.length} night ₹{nightSubtotal.toLocaleString()}</span>}
                </p>
              )}
              {discount > 0 ? (
                <p className="text-xs mt-0.5 flex items-center gap-1.5">
                  <span className="text-green-600 font-medium">🎁 Saving ₹{discount.toLocaleString()}</span>
                  <span className="text-[#f59e0b] font-black">Promo Applied ✓</span>
                </p>
              ) : promo.active && selectedSlots.length >= Number(promo.minSlots) && !consecutive ? (
                <p className="text-red-500 text-xs mt-0.5">
                  Select consecutive slots to unlock the offer
                </p>
              ) : promo.active && selectedSlots.length < Number(promo.minSlots) ? (
                <p className="text-[#b45309] text-xs mt-0.5">
                  {Number(promo.minSlots) - selectedSlots.length} more hr{Number(promo.minSlots) - selectedSlots.length > 1 ? "s" : ""} to unlock deal
                </p>
              ) : null}
            </div>

            {/* Right: total + CTA */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                {discount > 0 && (
                  <p className="text-black/30 text-xs line-through leading-none">₹{rawTotal.toLocaleString()}</p>
                )}
                <p className="text-black font-black text-xl leading-tight">
                  ₹{finalTotal.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => navigate("/booking-form")}
                className="bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-sm px-6 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-[#f59e0b]/25 hover:-translate-y-0.5 whitespace-nowrap"
              >
                Continue to Book →
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
